import * as Immutable from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button } from 'semantic-ui-react';

import {
  getFastExpressionLanguage,
  getLanguageFields,
  LanguageField,
  LanguageFieldArguments,
  setTabCodeError,
} from '../../../actions/EditorActions/EditorActions';
import { getSelectionCode } from '../../../actions/Simulation/DataAction';
import { toggleMonacoInit } from '../../../actions/WalkthroughAction';
import { StateType } from '../../../reducers';
import { getUserIdFromUserSelector } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import {
  activeTabSelector,
  codeEditorWidthSelector,
  delaySelector,
  getCurrentCode,
  getCurrentLanguage,
  instrumentTypeSelector,
  regionSelector,
  universeSelector,
} from '../../../reducers/EditorReducer/EditorSelector';
import { fastExpressionOperatorsSelector } from '../../../reducers/FastExpressionLanguageReducer/FastExpressionLanguageSelector';
import {
  languageComboIdsSelector,
  languageComboSelector,
  languageFieldIdsSelector,
  languageFieldsFetchingSelector,
  languageFieldsSelector,
  languageSelectionIdsSelector,
  languageSelectionSelector,
} from '../../../reducers/LanguageFieldsReducer/LanguageFieldsSelector';
import { fetchingSelectionCodeSelector } from '../../../reducers/SimulationReducer/DataReducer/DataSelector';
import { getIsSimulationWalkthroughComplete } from '../../../reducers/CustomPreferencesReducer/CustomPreferencesSelectors';
import { EMPTY_FUNCTION } from '../../../shared/constants';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { isMobile } from '../../../shared/helpers/responsive';
import { logErrorService } from '../../../shared/services';
import { DispatchConnect, ImmutableInterface } from '../../../types/';
import HOCIsMobile, { HOCIsMobileState } from '../../HOCIsMobile/HOCIsMobile';
import { Loader } from '../../Loader';

import { languageDefinition } from './lib/expressionDefinition';
import { fastExpLanguageDefinition } from './lib/fastExpressionDefinition';
import pythonDefinition from './lib/pythonDefinition';
import { pythonLanguageConfiguration, pythonMonarchLanguage } from './lib/pythonLanguage';
import './SourceCodeEditor.scss';
import SourceCodeEditorBottom from './SourceCodeEditorBottom';
import { fieldTypeOptions } from '../../../containers/DataExplorer/Filters/FilterFields/dataExplorerFieldsConfig';
import { registerMonacoLanguage } from '../../../shared/helpers/monaco-helper';
import classcat from 'classcat';
import { monaco } from 'react-monaco-editor';
import { Operator } from '../../../shared/api/api';

const ReactMonacoEditor = React.lazy(() => import('react-monaco-editor'));

interface SourceCodeEditorStateProps {
  currentLanguage: string;
  currentCode?: string;
  fastExpressionOperators?: Immutable.List<ImmutableInterface<Operator>>;
  userId: string;
  codeEditorWidth: number;
  languageFieldsRegular: any;
  languageFieldsSelection: any;
  languageFieldsCombo: any;
  languageFieldsRegularIds: string[];
  languageFieldsSelectionIds: string[];
  languageFieldsComboIds: string[];
  fetchingSelectionCode: boolean;
  activeTab: any;
  currentCombo: string;
  currentSelection: string;
  region: string;
  delay: number;
  instrument: string;
  universe: string;
  isLanguageFieldsFetching: boolean;
  isSimulationWalkthroughComplete?: boolean;
}

interface SourceCodeEditorProps {
  onCodeChange: (newValue: any, codeType: string) => void;
  handleMonacoRef: (ref: any) => void;
  isSimulating: boolean;
  handleCloneAlpha: () => void;
  setHandlerOnResize: (callback: any) => void;
  isSuperAlpha?: boolean;
  hasSelectionButton?: boolean;
  codeType?: string;
  currentCode?: string;
  themeColor: string;
  handlerThemeLight: () => void;
  handlerThemeDark: () => void;
  isFetchingCheck?: boolean;
  activeTab: any;
}

interface SourceCodeEditorStateDispatchers {
  handleGetFastExpression: () => Promise<any>;
  handleGetLanguageFields: (parameters: LanguageFieldArguments) => Promise<any>;
  handleToggleMonacoInit: () => void;
  handleCleanTabCodeError: (id: string, childTabIndex?: string) => void;
  dispatchGetSelectionCode: (
    id: string,
    selection: string,
    delay: any,
    region: string,
    instrumentType: string,
    selectionLimit: string,
    selectionHandling: string
  ) => void;
}

type SourceCodeEditorGeneric = SourceCodeEditorStateProps & SourceCodeEditorProps & SourceCodeEditorStateDispatchers & HOCIsMobileState;

const customLanguages = Immutable.fromJS([
  'expression-regular',
  'expression-selection',
  'expression-combo',
  'fastexpr-regular',
  'fastexpr-selection',
  'fastexpr-combo',
]);

class SourceCodeEditor extends React.Component<SourceCodeEditorGeneric> {
  private editor: any;
  private monaco: any;
  private monacoFastExpressionDisposable: any;

  componentDidMount() {
    this.props.setHandlerOnResize(() => isMobile('mobile'));

    if (!this.props.fastExpressionOperators) {
      const fastExpression = this.props.handleGetFastExpression();

      const { delay, region, instrument, universe } = this.props;

      const languageFields = this.props.handleGetLanguageFields({
        delay,
        region,
        instrument,
        universe,
      });

      Promise.all([fastExpression, languageFields])
        .then(() => {
          this.setTokensProvider();
        })
        .catch(error => {
          logErrorService(error, error);
        });
    }
  }

  setTokensProvider = () => {
    if (
      this.monaco &&
      this.monaco.languages &&
      this.monaco.languages.getLanguages().find((language: any) => customLanguages.includes(language.id))
    ) {
      const type = this.props.codeType ? this.props.codeType : 'regular';
      const languageId = `fastexpr-${type}`;

      const fieldsMap = this.getFieldsMap();
      const fieldIdsMap = this.getFieldIdMap();
      const fastExpressionOperators = this.getOperators(type);

      this.monaco.languages.setMonarchTokensProvider(languageId, languageDefinition(fastExpressionOperators, fieldIdsMap[type]));
      this.cleanproviderOptionsValues();
      this.updateItemProvider(fieldsMap[type], fastExpressionOperators, type);
    }
  };

  componentDidUpdate(prevProps: SourceCodeEditorGeneric) {
    if (prevProps.currentLanguage !== this.props.currentLanguage && this.editor) {
      const model = this.editor.getModel();
      if (model) {
        const type = this.props.codeType ? this.props.codeType : 'regular';
        const newLang = this.props.currentLanguage === 'python' ? 'python' : `${this.props.currentLanguage}-${type}`;
        monaco.editor.setModelLanguage(model, newLang);
      }
    }

    if (this.props.activeTab && this.editor && this.editor.setSelection) {
      const activeChildTab = this.props.activeTab.get('activeChildTab');
      const codeError =
        activeChildTab !== undefined
          ? this.props.activeTab.getIn(['childTabs', activeChildTab, 'codeError'])
          : this.props.activeTab.get('codeError');
      const prevCodeError =
        activeChildTab !== undefined
          ? prevProps.activeTab.getIn(['childTabs', activeChildTab, 'codeError'])
          : prevProps.activeTab.get('codeError');

      if (
        (!prevCodeError && codeError) ||
        this.props.activeTab.get('id') !== prevProps.activeTab.get('id') ||
        this.props.activeTab.get('activeChildTab') !== prevProps.activeTab.get('activeChildTab')
      ) {
        this.handleCodeEditorErrorsSelection(codeError);
      }
    }
    if ((this.props.activeTab !== prevProps.activeTab || this.props.isMobile !== prevProps.isMobile) && this.editor) {
      this.editor.updateOptions({
        value: this.props.isMobile && this.props.activeTab.get('childTabs'),
        readOnly: this.props.isMobile && this.props.activeTab.get('childTabs'),
      });
    }

    if (
      prevProps.codeType !== this.props.codeType ||
      prevProps.languageFieldsRegularIds !== this.props.languageFieldsRegularIds ||
      prevProps.languageFieldsSelectionIds !== this.props.languageFieldsSelectionIds ||
      prevProps.languageFieldsComboIds !== this.props.languageFieldsComboIds ||
      prevProps.fastExpressionOperators !== this.props.fastExpressionOperators
    ) {
      this.setTokensProvider();
    }
  }

  handleCodeEditorErrorsSelection(codeError: Immutable.Map<string, any> | null) {
    if (codeError) {
      const start: number = codeError.get('start');
      const end: number = codeError.get('end');
      const property: string = codeError.get('property');
      const startColumn = start !== undefined && start !== null ? start + 1 : end + 1;
      const endColumn = end !== undefined && end !== null ? end + 1 : start + 1;
      const line = codeError.get('line');
      const type = this.props.codeType ? this.props.codeType : 'regular';
      if (startColumn && endColumn && line && this.editor && this.monaco && type === property) {
        this.editor.setSelection(new this.monaco.Selection(line, startColumn, line, endColumn));
        this.editor.focus();
      }
    }
  }

  editorDidMount = (editor: any) => {
    this.editor = editor;
    if (this.props.isSimulationWalkthroughComplete && this.props.codeType !== 'combo') {
      editor.focus();
    }

    editor.getContribution('editor.contrib.suggestController').widget.value._setDetailsVisible(true);
    this.props.handleToggleMonacoInit();
  };

  cleanproviderOptionsValues = () => {
    if (this.monacoFastExpressionDisposable) {
      this.monacoFastExpressionDisposable.dispose();
    }
  };

  componentWillUnmount() {
    this.cleanproviderOptionsValues();
  }

  formatDocumentation = (field: LanguageField) => {
    let result = [`<h5>${field.id}</h5>`];
    if (field.type) {
      const formattedType = fieldTypeOptions.find(option => option.value === field.type)?.text;
      if (formattedType) {
        const subcategory = field.subcategory?.name;
        result.push(`<span><strong>Type</strong>: ${formattedType}</span>`);
        if (subcategory) {
          result.push(`, ${subcategory} `);
        }
        result.push('<br/><br/>');
      }
    }

    if (field.description) {
      result.push(`${field.description}`);
    }

    return {
      supportHtml: true,
      value: result.join('') ?? '',
    };
  };

  updateItemProvider = (languageFields: any, fastExpressionOperators: Operator[], type: string = 'regular') => {
    if (this.monaco) {
      const fieldsFastExpression = languageFields.map((field: LanguageField) => ({
        label: field.id,
        kind: this.monaco.languages.CompletionItemKind.Field,
        insertText: field.id,
        detail: 'read more',
        documentation: this.formatDocumentation(field),
      }));
      const operatorsFastExpression: Partial<monaco.languages.CompletionItem>[] = fastExpressionOperators.map(operator => ({
        label: operator.name,
        kind: this.monaco.languages.CompletionItemKind.Method,
        insertText: operator.name,
        documentation: {
          value: `<h5>${operator.definition}</h5> <span>${operator.description ?? ''}</span>`,
          supportHtml: true,
        },
      }));
      const completionItemsFastExpression = fieldsFastExpression.concat(operatorsFastExpression);

      this.monacoFastExpressionDisposable = this.monaco.languages.registerCompletionItemProvider(`fastexpr-${type}`, {
        provideCompletionItems: (model: any, position: any): any => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          completionItemsFastExpression.forEach((item: any) => (item.range = range));
          return {
            suggestions: completionItemsFastExpression,
          };
        },
      });
    }
  };

  editorWillMount = (monaco: any) => {
    this.monaco = monaco;
    const type = this.props.codeType ? this.props.codeType : 'regular';
    const languageId = `fastexpr-${type}`;

    registerMonacoLanguage(monaco, languageId);

    // Register Python language with its own configuration and monarch tokenizer
    if (!monaco.languages.getLanguages()?.find((lang: { id?: string }) => lang?.id === 'python')) {
      monaco.languages.register({ id: 'python' });
    }
    monaco.languages.setLanguageConfiguration('python', pythonLanguageConfiguration);
    monaco.languages.setMonarchTokensProvider('python', pythonMonarchLanguage);

    monaco.languages.registerCompletionItemProvider('python', {
      provideCompletionItems: () => ({
        suggestions: pythonDefinition(monaco),
      }),
    });

    const fieldsMap = this.getFieldsMap();
    const fieldIdsMap = this.getFieldIdMap();
    const fastExpressionOperators = this.getOperators(type);

    // Set fast expression code for regular, selection and combo
    monaco.languages.setMonarchTokensProvider(languageId, fastExpLanguageDefinition(fastExpressionOperators, fieldIdsMap[type]));

    if (!this.monacoFastExpressionDisposable) {
      this.updateItemProvider(fieldsMap[type], fastExpressionOperators, type);
    }

    const vsDarkerSettings: any = {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'invalid', foreground: '#ffb1af' },
        { token: 'string.invalid', foreground: '#ffb1af' },
        { token: 'comment', foreground: '#bdc4cc' },
        { token: 'number', foreground: '#a6e3b3' },
        { token: 'keyword', foreground: '#91cbff' },
        { token: 'type', foreground: '#ffb757' },
        { token: 'operator', foreground: '#ff9492' },
        { token: 'string', foreground: '#addcff' },
      ],
      colors: {
        'editor.background': '#000000',
        'editor.foreground': '#ffffff',
        'editorCursor.foreground': '#71b7ff',
        'editorLineNumber.foreground': '#9ea7b3',
        'symbolIcon.fieldForeground': '#91cbff',
        'symbolIcon.methodForeground': '#ffb757',
        'editorLineNumber.activeForeground': '#ffffff',
        // Bracket pair colorization
        'editorBracketHighlight.foreground1': '#91cbff',
        'editorBracketHighlight.foreground2': '#4ae168',
        'editorBracketHighlight.foreground3': '#f7c843',
        'editorBracketHighlight.foreground4': '#ffb1af',
        'editorBracketHighlight.foreground5': '#ffadd4',
        'editorBracketHighlight.foreground6': '#dbb7ff',
      },
    };

    monaco.editor.defineTheme('vs-dark', vsDarkerSettings);

    const lightThemeSettings: any = {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'invalid', foreground: '#b31d28' },
        { token: 'string.invalid', foreground: '#b31d28' },
        { token: 'comment', foreground: '#6a737d' },
        { token: 'number', foreground: '#098658' },
        { token: 'keyword', foreground: '#005cc5' },
        { token: 'type', foreground: '#c45308' },
        { token: 'operator', foreground: '#d73a49' },
        { token: 'string', foreground: '#032f62' },
      ],
      colors: {
        'editor.background': '#ffffff',
        'editor.foreground': '#24292e',
        'editorCursor.foreground': '#044289',
        'editorLineNumber.foreground': '#1b1f234d',
        'editorLineNumber.activeForeground': '#24292e',
        'symbolIcon.fieldForeground': '#005cc5',
        'symbolIcon.methodForeground': '#c45308',
        // Bracket pair colorization
        'editorBracketHighlight.foreground1': '#0969da',
        'editorBracketHighlight.foreground2': '#1a7f37',
        'editorBracketHighlight.foreground3': '#9a6700',
        'editorBracketHighlight.foreground4': '#cf222e',
        'editorBracketHighlight.foreground5': '#bf3989',
        'editorBracketHighlight.foreground6': '#8250df',
      },
    };

    monaco.editor.defineTheme('light-theme', lightThemeSettings);
  };

  private getFieldIdMap() {
    const fieldsRegularIds = this.props.languageFieldsRegularIds;
    const fieldsSelectionIds = this.props.languageFieldsSelectionIds;
    const fieldsComboIds = this.props.languageFieldsComboIds;

    const fieldIdsMap: any = {
      regular: fieldsRegularIds,
      selection: fieldsSelectionIds,
      combo: fieldsComboIds,
    };
    return fieldIdsMap;
  }

  private getFieldsMap() {
    const fieldsRegular = this.props.languageFieldsRegular ? this.props.languageFieldsRegular.toJS() : [];
    const fieldsSelection = this.props.languageFieldsSelection ? this.props.languageFieldsSelection.toJS() : [];
    const fieldsCombo = this.props.languageFieldsCombo ? this.props.languageFieldsCombo.toJS() : [];
    const fieldsMap: any = {
      regular: fieldsRegular,
      selection: fieldsSelection,
      combo: fieldsCombo,
    };
    return fieldsMap;
  }

  private getOperators(type: string) {
    return this.props.fastExpressionOperators
      ? (this.props.fastExpressionOperators.filter(operator => operator.get('scope').includes(type.toUpperCase())).toJS() as Operator[])
      : [];
  }

  handleCleanTabCodeError = () => {
    const id = this.props.activeTab.get('id');
    const childIndex = this.props.activeTab.get('childTabs') ? this.props.activeTab.get('activeChildTab') : null;
    this.props.handleCleanTabCodeError(id, childIndex);
  };

  onCodeChange = (newValue: any, _: any) => {
    this.props.onCodeChange(newValue, this.props.codeType ? this.props.codeType : 'code');
  };

  handlerSelectionCode = () => {
    const activeTab = this.props.activeTab;
    if (activeTab) {
      const region = activeTab.getIn(['settings', 'region', 'value']);
      const delay = activeTab.getIn(['settings', 'delay', 'value']);
      const instrumentType = activeTab.getIn(['settings', 'instrumentType', 'value']);
      const selectionLimit = activeTab.getIn(['settings', 'selectionLimit', 'value']);
      const selectionHandling = activeTab.getIn(['settings', 'selectionHandling', 'value']);
      const selection = encodeURIComponent(activeTab.get('selection'));
      const id = activeTab.get('id');
      this.props.dispatchGetSelectionCode(id, selection, delay, region, instrumentType, selectionLimit, selectionHandling);
    }
  };

  shouldDisableClone() {
    const { currentCode, currentCombo, currentSelection, activeTab, codeType } = this.props;
    const disableSuper = !(currentCombo && currentCombo.trim().length > 0 && currentSelection && currentSelection.trim().length > 0);
    const disableRegular = !((currentCode && currentCode.trim().length > 0) || activeTab.get('childTabs'));

    if (activeTab.get('childTabs') && activeTab.get('childTabs').size > 9) {
      return true;
    } else {
      return codeType ? disableSuper : disableRegular;
    }
  }

  render() {
    let modifiers = this.props.themeColor === 'light-theme' ? ['light'] : ['dark'];
    const { isSuperAlpha = false, hasSelectionButton = false, isLanguageFieldsFetching } = this.props;
    const type = this.props.codeType ? this.props.codeType : 'regular';
    modifiers = modifiers.concat(type);
    const monacoLanguage = this.props.currentLanguage === 'python' ? 'python' : `${this.props.currentLanguage}-${type}`;

    return (
      <div
        className={classcat([
          addModifiers('intro-step-3 editor-code__source-code', modifiers),
          isLanguageFieldsFetching && 'monaco-editor--fields-loading',
        ])}
      >
        <React.Suspense fallback={<Loader active={true} inline="centered" size="massive" />}>
          <ReactMonacoEditor
            // key is required to force the editor to re-mount when switching tabs, due to a monaco bug
            // check if it's still necessary when updating monaco version
            key={this.props.activeTab.get('id')}
            language={monacoLanguage}
            theme={this.props.themeColor}
            value={this.props.isMobile && this.props.activeTab.get('childTabs') ? '' : this.props.currentCode}
            options={{
              selectOnLineNumbers: true,
              quickSuggestions: true,
              automaticLayout: true,
              scrollBeyondLastLine: false,
              lineHeight: 20,
              // iOS Safari auto-zooms when focusing an input with font-size < 16px.
              // Monaco defaults to 14px, so bump it to 16px on mobile to prevent the zoom.
              ...(this.props.isMobile ? { fontSize: 16 } : {}),
              readOnly: this.props.isMobile && this.props.activeTab.get('childTabs'),
              minimap: {
                enabled: false,
              },

              scrollbar: {
                useShadows: false,
                verticalHasArrows: false,
                vertical: 'visible',
                verticalScrollbarSize: 6,
              },
              wordWrap: 'wordWrapColumn',
              wordWrapColumn: (this.props.codeEditorWidth - 40) / 7.8,
              wrappingIndent: 'none',
            }}
            onChange={this.onCodeChange}
            editorDidMount={this.editorDidMount}
            editorWillMount={this.editorWillMount}
            editorWillUnmount={EMPTY_FUNCTION}
          />
        </React.Suspense>
        {!isSuperAlpha && (
          <SourceCodeEditorBottom
            handlerThemeLight={this.props.handlerThemeLight}
            handlerThemeDark={this.props.handlerThemeDark}
            handleCleanTabCodeError={this.handleCleanTabCodeError}
            handleCloneAlpha={this.props.handleCloneAlpha}
            isSimulating={this.props.isSimulating}
            isMobile={this.props.isMobile}
            activeTab={this.props.activeTab}
            shouldDisableClone={this.shouldDisableClone()}
          />
        )}
        {hasSelectionButton && (
          <Button
            onClick={this.handlerSelectionCode}
            className="button--tangerine button--small editor-code__bottom--selection"
            disabled={!this.props.currentCode || this.props.fetchingSelectionCode || this.props.isFetchingCheck}
          >
            <FormattedMessageHelper id="editor.code.selection" />
          </Button>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: StateType, props: SourceCodeEditorProps): SourceCodeEditorStateProps => ({
  activeTab: activeTabSelector(state),
  languageFieldsRegular: languageFieldsSelector(state),
  languageFieldsSelection: languageSelectionSelector(state),
  languageFieldsCombo: languageComboSelector(state),

  languageFieldsRegularIds: languageFieldIdsSelector(state),
  languageFieldsSelectionIds: languageSelectionIdsSelector(state),
  languageFieldsComboIds: languageComboIdsSelector(state),

  fastExpressionOperators: fastExpressionOperatorsSelector(state),
  currentLanguage: getCurrentLanguage(state),
  // @ts-ignore
  userId: getUserIdFromUserSelector(state),
  isSimulationWalkthroughComplete: getIsSimulationWalkthroughComplete(state),
  fetchingSelectionCode: fetchingSelectionCodeSelector(state),
  currentCode: getCurrentCode(state, props.codeType ? props.codeType : 'code'),
  // @ts-ignore
  codeEditorWidth: codeEditorWidthSelector(state),
  currentCombo: getCurrentCode(state, 'combo'),
  currentSelection: getCurrentCode(state, 'selection'),
  delay: delaySelector(state),
  instrument: instrumentTypeSelector(state),
  universe: universeSelector(state),
  region: regionSelector(state),
  isLanguageFieldsFetching: languageFieldsFetchingSelector(state) as boolean,
});

function mapDispatchToProps(dispatch: Dispatch<any>): SourceCodeEditorStateDispatchers {
  return {
    handleGetFastExpression: async () => dispatch(getFastExpressionLanguage()),
    handleGetLanguageFields: async ({ delay, region, instrument, universe }) =>
      dispatch(getLanguageFields({ delay, region, instrument, universe })),
    handleToggleMonacoInit: () => {
      dispatch(toggleMonacoInit());
    },
    handleCleanTabCodeError: (id: string, childTabIndex?: string) => {
      dispatch(setTabCodeError(null, id, childTabIndex));
    },
    dispatchGetSelectionCode: async (
      id: string,
      selection: string,
      delay: any,
      region: string,
      instrumentType: string,
      selectionLimit: string,
      selectionHandling: string
    ) => dispatch(getSelectionCode(id, selection, delay, region, instrumentType, selectionLimit, selectionHandling)),
  };
}

export default connect<SourceCodeEditorStateProps, DispatchConnect<SourceCodeEditorStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(HOCIsMobile(SourceCodeEditor));
